/**
 * PrintTemplate - Dedicated print-optimized templates
 * Renders clean, print-ready forms without any UI chrome
 */

// Get optimal row counts based on orientation and spacing
const getRowCounts = (orientation, spacing, layoutType) => {
  if (orientation === 'landscape') {
    if (layoutType === 'date-rows-domain-columns') {
      return spacing === 'compact' ? 8 : spacing === 'spacious' ? 4 : 6;
    }
    return spacing === 'compact' ? 10 : spacing === 'spacious' ? 5 : 7;
  } else {
    if (layoutType === 'date-rows-domain-columns') {
      return spacing === 'compact' ? 16 : spacing === 'spacious' ? 8 : 12;
    }
    return spacing === 'compact' ? 18 : spacing === 'spacious' ? 10 : 14;
  }
};

// Get optimal column counts
const getDateColumnCount = (orientation, spacing) => {
  if (orientation === 'landscape') {
    return spacing === 'compact' ? 14 : spacing === 'spacious' ? 10 : 12;
  } else {
    return spacing === 'compact' ? 10 : spacing === 'spacious' ? 6 : 8;
  }
};

export const PrintTemplate = ({
  rubric,
  outputType,
  layoutType,
  orientation,
  spacing,
  includeNameField,
  includeDateField,
  includeTherapistField,
  studentName,
  therapistName,
  isPreview = false
}) => {
  if (!rubric) return null;

  // Calculate sizing
  const dateColumnCount = getDateColumnCount(orientation, spacing);
  const rowCount = getRowCounts(orientation, spacing, layoutType);
  
  // Cell sizing - tighter for print
  const cellPadding = spacing === 'compact' ? '1px 3px' : spacing === 'spacious' ? '4px 6px' : '2px 4px';
  const cellHeight = spacing === 'compact' ? '16px' : spacing === 'spacious' ? '24px' : '20px';
  const fontSize = spacing === 'compact' ? '8pt' : spacing === 'spacious' ? '10pt' : '9pt';
  const headerFontSize = spacing === 'compact' ? '9pt' : spacing === 'spacious' ? '11pt' : '10pt';

  // Common styles
  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize,
    tableLayout: 'fixed'
  };

  const thStyle = {
    border: '1px solid #000',
    padding: cellPadding,
    fontWeight: '600',
    fontSize: headerFontSize,
    backgroundColor: isPreview ? '#f5f5f5' : 'transparent',
    verticalAlign: 'middle'
  };

  const tdStyle = {
    border: '1px solid #000',
    padding: cellPadding,
    verticalAlign: 'middle'
  };

  // Render header with title and fields
  const renderHeader = () => {
    // Check if rubric has goals array (new format) or just goal_area (old format)
    const hasGoals = rubric.goals && rubric.goals.length > 0;
    
    return (
      <div style={{ marginBottom: '6px' }}>
        <div style={{ 
          fontSize: '13pt', 
          fontWeight: '700', 
          marginBottom: '1px',
          lineHeight: '1.2'
        }}>
          {rubric.title}
        </div>
        
        {/* Goals section - numbered list */}
        {hasGoals ? (
          <div style={{ fontSize: '8pt', marginBottom: '3px', color: '#333' }}>
            <div style={{ fontWeight: '600', marginBottom: '2px' }}>Goals:</div>
            <ol style={{ margin: '0', paddingLeft: '16px' }}>
              {rubric.goals.map((goal, index) => (
                <li key={goal.id || index} style={{ marginBottom: '1px' }}>
                  {goal.text}
                </li>
              ))}
            </ol>
          </div>
        ) : rubric.goal_area ? (
          <div style={{ fontSize: '8pt', marginBottom: '3px', color: '#333' }}>
            {rubric.goal_area}
          </div>
        ) : null}
        
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '16px', 
          fontSize: '9pt',
          marginTop: '3px'
        }}>
          {includeNameField && (
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <span style={{ fontWeight: '600', marginRight: '4px' }}>Student:</span>
              {studentName ? (
                <span>{studentName}</span>
              ) : (
                <span style={{ 
                  display: 'inline-block', 
                  width: '100px', 
                  borderBottom: '1px solid #000',
                  minHeight: '12px'
                }}></span>
              )}
            </div>
          )}
          {includeDateField && (
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <span style={{ fontWeight: '600', marginRight: '4px' }}>Date:</span>
              <span style={{ 
                display: 'inline-block', 
                width: '70px', 
                borderBottom: '1px solid #000',
                minHeight: '12px'
              }}></span>
            </div>
          )}
          {includeTherapistField && (
            <div style={{ display: 'flex', alignItems: 'baseline' }}>
              <span style={{ fontWeight: '600', marginRight: '4px' }}>SLP:</span>
              {therapistName ? (
                <span>{therapistName}</span>
              ) : (
                <span style={{ 
                  display: 'inline-block', 
                  width: '80px', 
                  borderBottom: '1px solid #000',
                  minHeight: '12px'
                }}></span>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render score key (compact single line)
  const renderScoreKey = () => (
    <div style={{ 
      fontSize: '7pt', 
      marginBottom: '4px',
      paddingBottom: '3px',
      borderBottom: '1px solid #999'
    }}>
      <strong style={{ marginRight: '4px' }}>KEY:</strong>
      {rubric.levels.map((level, i) => (
        <span key={level.level_value} style={{ marginRight: '8px' }}>
          <strong>{level.level_value}</strong>={level.level_label}
        </span>
      ))}
    </div>
  );

  // Domain rows with date columns (portrait optimized)
  const renderDomainRowsPortrait = () => {
    const cols = dateColumnCount;
    
    return (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '22%', textAlign: 'left' }}>Domain/Criteria</th>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={`portrait-header-${i}`} style={{ ...thStyle, textAlign: 'center', padding: '1px' }}>
                <div style={{ 
                  borderBottom: '1px solid #666', 
                  height: '14px',
                  margin: '0 2px'
                }}></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rubric.domains.map((domain) => (
            <tr key={domain.id || domain.domain_name}>
              <td style={{ ...tdStyle, fontWeight: '500', fontSize: '8pt' }}>
                {domain.domain_name}
              </td>
              {Array.from({ length: cols }).map((_, i) => (
                <td key={`portrait-${domain.id || domain.domain_name}-${i}`} style={{ ...tdStyle, padding: '1px' }}>
                  <div style={{ height: cellHeight }}></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Domain rows with date columns (landscape optimized - more columns)
  const renderDomainRowsLandscape = () => {
    const cols = dateColumnCount;
    
    return (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '18%', textAlign: 'left' }}>Domain/Criteria</th>
            {Array.from({ length: cols }).map((_, i) => (
              <th key={`landscape-header-${i}`} style={{ ...thStyle, textAlign: 'center', padding: '1px' }}>
                <div style={{ 
                  borderBottom: '1px solid #666', 
                  height: '12px',
                  margin: '0 1px'
                }}></div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rubric.domains.map((domain) => (
            <tr key={domain.id || domain.domain_name}>
              <td style={{ ...tdStyle, fontWeight: '500', fontSize: '8pt' }}>
                {domain.domain_name}
              </td>
              {Array.from({ length: cols }).map((_, i) => (
                <td key={`landscape-${domain.id || domain.domain_name}-${i}`} style={{ ...tdStyle, padding: '1px' }}>
                  <div style={{ height: cellHeight }}></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Date rows with domain columns
  const renderDateRowsDomainColumns = () => {
    const domainCount = rubric.domains.length;
    const domainWidth = `${Math.floor(85 / domainCount)}%`;
    
    return (
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '12%', textAlign: 'left' }}>Date</th>
            {rubric.domains.map((domain) => (
              <th key={domain.id || domain.domain_name} style={{ 
                ...thStyle, 
                width: domainWidth,
                textAlign: 'center',
                fontSize: '7pt',
                padding: '2px'
              }}>
                {domain.domain_name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rowCount }).map((_, rowIdx) => (
            <tr key={`daterow-${rowIdx}`}>
              <td style={tdStyle}>
                <div style={{ 
                  borderBottom: '1px solid #666', 
                  height: cellHeight 
                }}></div>
              </td>
              {rubric.domains.map((domain, domainIdx) => (
                <td key={`datecell-${domain.id || domain.domain_name}-${domainIdx}`} style={{ ...tdStyle, padding: '1px' }}>
                  <div style={{ height: cellHeight }}></div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Full rubric with all descriptors
  const renderFullRubric = () => {
    const levelCount = rubric.levels.length;
    const levelWidth = `${Math.floor(78 / levelCount)}%`;
    
    return (
      <table style={{ ...tableStyle, fontSize: spacing === 'compact' ? '7pt' : '8pt' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '22%', textAlign: 'left' }}>Domain/Criteria</th>
            {rubric.levels.map((level) => (
              <th key={level.level_value} style={{ 
                ...thStyle, 
                width: levelWidth,
                textAlign: 'center',
                verticalAlign: 'bottom'
              }}>
                <div style={{ fontWeight: '700', fontSize: '9pt' }}>{level.level_value}</div>
                <div style={{ fontSize: '7pt', fontWeight: '500' }}>{level.level_label}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rubric.domains.map((domain) => (
            <tr key={domain.id || domain.domain_name}>
              <td style={{ ...tdStyle, fontWeight: '600', verticalAlign: 'top' }}>
                {domain.domain_name}
              </td>
              {rubric.levels.map((level, levelIdx) => (
                <td key={`desc-${domain.id || domain.domain_name}-${level.level_value}`} style={{ 
                  ...tdStyle, 
                  verticalAlign: 'top',
                  fontSize: '7pt',
                  lineHeight: '1.2'
                }}>
                  {domain.level_descriptors?.[levelIdx] || level.level_description || ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  // Select the right layout renderer
  const renderContent = () => {
    if (outputType === 'full-rubric') {
      return renderFullRubric();
    }

    if (layoutType === 'date-rows-domain-columns') {
      return renderDateRowsDomainColumns();
    }
    
    // Default: domain-rows-date-columns
    return orientation === 'landscape' 
      ? renderDomainRowsLandscape() 
      : renderDomainRowsPortrait();
  };

  // Preview version (shown on screen)
  if (isPreview) {
    return (
      <div style={{ 
        fontFamily: "'Work Sans', Arial, sans-serif",
        color: '#000',
        fontSize: '10pt',
        lineHeight: '1.3'
      }}>
        {renderHeader()}
        {outputType === 'with-key' && renderScoreKey()}
        {renderContent()}
      </div>
    );
  }

  // Print version (hidden on screen, shown during print)
  return (
    <div 
      className="print-template"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        width: orientation === 'landscape' ? '10.2in' : '7.7in',
        backgroundColor: '#fff',
        fontFamily: "'Work Sans', Arial, sans-serif",
        color: '#000',
        fontSize: '10pt',
        lineHeight: '1.3',
        padding: 0,
        margin: 0
      }}
    >
      {renderHeader()}
      {outputType === 'with-key' && renderScoreKey()}
      {renderContent()}
    </div>
  );
};

export default PrintTemplate;
